import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Checkbox } from "@/components/ui/Checkbox/Checkbox";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Banner, Field, FormShell } from "@/components/auth/FormShell";
import { PasswordStrengthBar, scorePassword } from "@/components/auth/PasswordStrength";
import { SsoRow } from "@/components/auth/SsoRow";
import formStyles from "@/components/auth/AuthForm.module.css";
import { signUp, type AuthError } from "@/lib/auth";

/**
 * Public sign-up form. Submitting calls Cognito SignUp, which creates
 * the user in UNCONFIRMED state and emails a 6-digit code. After the
 * code lands the user types it on /signup/verify; the PostConfirmation
 * Lambda then disables the user (waitlist gate) and provisions their
 * pending workspace from `custom:initialWorkspaceName`.
 */
export function SignUp() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { meetsLength, meetsCase, meetsNumber } = scorePassword(password);
  const canSubmit =
    agreed &&
    !!email.trim() &&
    !!workspaceName.trim() &&
    meetsLength &&
    meetsCase &&
    meetsNumber;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await signUp({
        email: email.trim(),
        password,
        workspaceName: workspaceName.trim(),
      });
      navigate(`/signup/verify?email=${encodeURIComponent(email.trim())}`, { replace: true });
    } catch (err) {
      setError(err as AuthError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout view="signup">
      <FormShell
        title="Join the Notamen waitlist"
        subtitle="No card. We approve workspaces in waves so every onboarding is hands-on."
      >
        <SsoRow next="/app" />
        <form onSubmit={onSubmit} noValidate className={formStyles.stack}>
          {error && error.kind === "user_exists" && (
            <Banner variant="info" icon={<Icon name="info" size={14} />}>
              {error.message}{" "}
              <Link to="/signin" className={formStyles.termsLink}>
                Sign in
              </Link>{" "}
              ·{" "}
              <Link
                to={`/signup/verify?email=${encodeURIComponent(email.trim())}`}
                className={formStyles.termsLink}
              >
                Enter code
              </Link>
            </Banner>
          )}
          {error && error.kind !== "user_exists" && (
            <Banner variant="danger" icon={<Icon name="info" size={14} />}>
              {error.message}
            </Banner>
          )}
          <Field
            label="Work email"
            hint="We’ll email a 6-digit code to verify ownership."
            htmlFor="signup-email"
          >
            <Input
              id="signup-email"
              icon="user"
              type="email"
              autoComplete="email"
              inputMode="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@brand.com"
              required
            />
          </Field>
          <Field
            label="Workspace name"
            hint="Your team and brands will live under this. Change it later from settings."
            htmlFor="signup-workspace"
          >
            <Input
              id="signup-workspace"
              type="text"
              autoComplete="organization"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder="Acme Studio"
              required
              maxLength={80}
            />
          </Field>
          <Field
            label="Password"
            hint="At least 12 characters, upper + lower case, and a number."
            htmlFor="signup-password"
          >
            <Input
              id="signup-password"
              icon="lock"
              type="password"
              autoComplete="new-password"
              suffix="show"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 12 characters"
              required
              minLength={12}
            />
            {password.length > 0 && <PasswordStrengthBar password={password} />}
          </Field>
          <Checkbox
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            label={
              <>
                I agree to the{" "}
                <a href="/terms" className={formStyles.termsLink}>
                  Terms
                </a>{" "}
                and{" "}
                <a href="/privacy" className={formStyles.termsLink}>
                  Privacy Policy
                </a>
                . We won’t email anything except onboarding + product updates.
              </>
            }
          />
          <Btn
            type="submit"
            variant="primary"
            full
            size="lg"
            loading={submitting}
            disabled={!canSubmit}
            iconRight={<Icon name="arrow-right" size={14} />}
          >
            Join the waitlist
          </Btn>
          <Banner variant="waitlist" icon={<Icon name="zap" size={14} color="var(--accent)" />}>
            Pricing starts when you’re invited off the list. Solo $29 · Studio $79 · Agency
            $249 — full details in your invite email.
          </Banner>
        </form>
      </FormShell>
    </AuthLayout>
  );
}
